import { Box, Container, Typography, Grid, Card, CardContent, Chip, Button, alpha, useTheme } from '@mui/material'
import {
  Business,
  Email,
  Dashboard,
  Category,
  AccessTime,
  Group,
  Lock,
  BarChart,
  CloudUpload,
  Notifications,
  Security,
  AutoAwesome,
  IntegrationInstructions,
  PhoneIphone,
  AttachFile,
  Share,
  Verified,
  Speed,
  TrendingUp,
  EmojiEvents,
  ArrowForward
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

interface Feature {
  icon: React.ReactNode
  title: string
  description: string
  highlights?: string[]
}

interface FeatureCategory {
  category: string
  tagline: string
  features: Feature[]
}

export default function FeaturesPage() {
  const theme = useTheme()
  const navigate = useNavigate()

  const featureCategories: FeatureCategory[] = [
    {
      category: 'Multi-Tenancy Architecture',
      tagline: 'Built specifically for MSPs supporting multiple client organizations',
      features: [
        {
          icon: <Business sx={{ fontSize: 40 }} />,
          title: 'Three-Tier Hierarchy',
          description: 'Unique architecture: Tenant (Your MSP) → Organizations (Your Clients) → Sites (Branches/Departments)',
          highlights: ['Unlimited client organizations', 'Unlimited child sites per org', 'Independent ticket numbering per tenant', 'Complete data isolation']
        },
        {
          icon: <Email sx={{ fontSize: 40 }} />,
          title: 'Unique Email Per Site',
          description: 'Every organization and child site gets its own support email address for automatic routing',
          highlights: ['Automatic ticket creation', 'Smart routing', 'No complex email rules', 'Email threading support']
        },
        {
          icon: <Group sx={{ fontSize: 40 }} />,
          title: 'Oversight Users',
          description: 'Client IT managers get cross-site visibility within their organization',
          highlights: ['Dashboard of all branches', 'Limited ticket creation', 'Organization-level analytics', 'Multi-site access control']
        }
      ]
    },
    {
      category: 'Ticket Management',
      tagline: 'Everything you need to manage customer support efficiently',
      features: [
        {
          icon: <Dashboard sx={{ fontSize: 40 }} />,
          title: 'Kanban Board View',
          description: 'Drag-and-drop ticket management across status columns',
          highlights: ['Visual workflow', 'Real-time updates', 'Queue management', 'Multi-status tracking']
        },
        {
          icon: <Category sx={{ fontSize: 40 }} />,
          title: 'Smart Categorization',
          description: 'Flexible category system with global, organization-specific, and site-specific categories',
          highlights: ['Color-coded categories', 'Multi-site assignment', 'Custom categories per client', 'Visual ticket sorting']
        },
        {
          icon: <Share sx={{ fontSize: 40 }} />,
          title: 'Group Tickets & Watchers',
          description: 'Collaborative tickets that entire departments can follow and receive updates',
          highlights: ['Department-wide visibility', 'Watcher subscriptions', 'Auto-notifications', 'Perfect for outages']
        },
        {
          icon: <Lock sx={{ fontSize: 40 }} />,
          title: 'Private Tickets',
          description: 'Mark sensitive tickets as private for requester-only visibility',
          highlights: ['HR issues', 'Security concerns', 'HIPAA compliance', 'Configurable per org']
        }
      ]
    },
    {
      category: 'SLA Management',
      tagline: 'Automated SLA tracking to ensure timely responses and resolutions',
      features: [
        {
          icon: <AccessTime sx={{ fontSize: 40 }} />,
          title: 'Business Hours Aware',
          description: 'SLA clock only counts down during your configured business hours',
          highlights: ['Customizable hours per tenant', 'Configurable working days', 'Weekend & night exclusion', 'Timezone support']
        },
        {
          icon: <TrendingUp sx={{ fontSize: 40 }} />,
          title: 'Multi-Level Escalation',
          description: 'Define up to 3 escalation levels with automatic triggers',
          highlights: ['SLA-based triggers', 'Priority-based escalation', 'Manual escalation', 'Complete audit trail']
        },
        {
          icon: <BarChart sx={{ fontSize: 40 }} />,
          title: 'SLA Analytics',
          description: 'Real-time compliance tracking and breach detection',
          highlights: ['Compliance percentage', 'Breach trends', 'Agent performance', 'At-risk ticket alerts']
        }
      ]
    },
    {
      category: 'Security & Compliance',
      tagline: 'Enterprise-grade security suitable for public sector and regulated industries',
      features: [
        {
          icon: <Security sx={{ fontSize: 40 }} />,
          title: 'Azure Front Door + WAF',
          description: 'DDoS protection, OWASP Top 10 coverage, bot protection, and rate limiting',
          highlights: ['DDoS mitigation', 'SQL injection protection', 'XSS prevention', 'Bot blocking']
        },
        {
          icon: <Verified sx={{ fontSize: 40 }} />,
          title: 'Mandatory MFA',
          description: 'TOTP-based multi-factor authentication required for all administrators',
          highlights: ['Google Authenticator compatible', 'Recovery codes', 'Encrypted secrets', 'QR code setup']
        },
        {
          icon: <Lock sx={{ fontSize: 40 }} />,
          title: 'Penetration Tested',
          description: 'Professional security audit completed with all critical findings remediated',
          highlights: ['UK Cyber Essentials aligned', 'NHS Data Security standards', 'OWASP compliant', 'Annual testing']
        },
        {
          icon: <CloudUpload sx={{ fontSize: 40 }} />,
          title: 'Data Encryption',
          description: 'AES-256 encryption at rest, TLS 1.2+ in transit',
          highlights: ['Database encryption', 'File storage encryption', 'Secure connections', 'Private endpoints']
        }
      ]
    },
    {
      category: 'Integrations & Automation',
      tagline: 'Connect with your existing tools and automate workflows',
      features: [
        {
          icon: <IntegrationInstructions sx={{ fontSize: 40 }} />,
          title: 'Microsoft Teams',
          description: 'Real-time notifications to Teams channels for ticket events',
          highlights: ['Configurable webhooks', 'Rich card format', 'Event triggers', 'Per-organization setup']
        },
        {
          icon: <AutoAwesome sx={{ fontSize: 40 }} />,
          title: 'Zapier Integration',
          description: 'Connect to 5,000+ apps with webhook events',
          highlights: ['Custom workflows', 'CRM sync', 'Project management', 'Data automation']
        },
        {
          icon: <Email sx={{ fontSize: 40 }} />,
          title: 'Email-to-Ticket',
          description: 'Automatic ticket creation from Microsoft 365 shared mailbox',
          highlights: ['Auto user provisioning', 'Email threading', 'Reply tracking', 'Smart routing']
        },
        {
          icon: <Speed sx={{ fontSize: 40 }} />,
          title: 'Real-Time Updates',
          description: 'SignalR-powered live updates without page refresh',
          highlights: ['Typing indicators', 'Live status changes', 'Multi-user awareness', 'Instant sync']
        }
      ]
    },
    {
      category: 'Analytics & Reporting',
      tagline: 'Comprehensive insights into your support operations',
      features: [
        {
          icon: <BarChart sx={{ fontSize: 40 }} />,
          title: 'Real-Time Dashboard',
          description: 'Live metrics on ticket volume, status distribution, and trends',
          highlights: ['30-day trends', 'Priority analysis', 'Response times', 'SLA compliance']
        },
        {
          icon: <EmojiEvents sx={{ fontSize: 40 }} />,
          title: 'Agent Performance',
          description: 'Track individual and team performance metrics',
          highlights: ['Resolution rates', 'Average resolution time', 'Workload distribution', 'Activity tracking']
        },
        {
          icon: <Business sx={{ fontSize: 40 }} />,
          title: 'Multi-Level Analytics',
          description: 'Reports at tenant, organization, and site levels',
          highlights: ['Per-client reporting', 'Site breakdown', 'Category analysis', 'Trend identification']
        }
      ]
    },
    {
      category: 'Customer Experience',
      tagline: 'Self-service portal for your customers to manage their tickets',
      features: [
        {
          icon: <PhoneIphone sx={{ fontSize: 40 }} />,
          title: 'Mobile-Responsive Portal',
          description: 'Branded customer portal accessible from any device',
          highlights: ['Self-service submission', 'Real-time tracking', 'Comment threads', 'Ticket history']
        },
        {
          icon: <AttachFile sx={{ fontSize: 40 }} />,
          title: 'File Attachments',
          description: 'Multiple file uploads with virus scanning and secure storage',
          highlights: ['Up to 50MB per file', 'Image preview', 'Microsoft Defender scan', 'Azure Blob Storage']
        },
        {
          icon: <Notifications sx={{ fontSize: 40 }} />,
          title: 'Smart Notifications',
          description: 'Intelligent email notifications for all ticket events',
          highlights: ['Status updates', 'New comments', 'Assignment alerts', 'Customizable preferences']
        }
      ]
    }
  ]

  return (
    <>
      <Helmet>
        <title>Features | Taylin Helpdesk - Purpose-Built for MSPs</title>
        <meta
          name="description"
          content="Comprehensive helpdesk features for MSPs: multi-tenant architecture, SLA management, group tickets, security & compliance, integrations, and more. Built specifically for managing multiple client organizations."
        />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 8
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Everything MSPs Need
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.95 }}>
              Purpose-built features for managing multi-site organizations
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label="Multi-Tenant"
                sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', fontWeight: 'bold' }}
              />
              <Chip
                label="SLA Management"
                sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', fontWeight: 'bold' }}
              />
              <Chip
                label="Enterprise Security"
                sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', fontWeight: 'bold' }}
              />
              <Chip
                label="Azure Marketplace"
                sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', fontWeight: 'bold' }}
              />
            </Box>
          </Container>
        </Box>

        {/* Features Grid */}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          {featureCategories.map((categoryData, index) => (
            <Box key={index} sx={{ mb: 8 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {categoryData.category}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {categoryData.tagline}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {categoryData.features.map((feature, featureIndex) => (
                  <Grid item xs={12} md={6} key={featureIndex}>
                    <Card
                      sx={{
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: theme.shadows[8]
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Box
                            sx={{
                              color: theme.palette.primary.main,
                              flexShrink: 0
                            }}
                          >
                            {feature.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                              {feature.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {feature.description}
                            </Typography>
                            {feature.highlights && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {feature.highlights.map((highlight, i) => (
                                  <Typography
                                    key={i}
                                    variant="caption"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: 'text.secondary'
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        bgcolor: theme.palette.primary.main,
                                        mr: 1,
                                        flexShrink: 0
                                      }}
                                    />
                                    {highlight}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          {/* CTA Section */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
              color: 'white'
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Ready to Transform Your Support Operations?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
              Start your 14-day free trial. No credit card required.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/signup')}
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.9)
                  },
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: alpha('#fff', 0.1)
                  },
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem'
                }}
              >
                Learn More
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  )
}
